/* eslint-disable react-hooks/exhaustive-deps */
import {useQuery} from "@tanstack/react-query"
import {Call as TwilioCall, Device} from "@twilio/voice-sdk"
import {Call as TelnyxCall, TelnyxRTC} from "@telnyx/webrtc"
import {handleError, toastError, toastInfo} from "helpers"
import {isBrowserSoftphone} from "helpers/voiceDialer"
import {SocketEvent} from "helpers/socket"
import {useModel} from "hooks"
import {createContext, useCallback, useEffect, useRef, useState} from "react"
import {autoDialerService, coreSocket, studentCallLogService, zoomService} from "services"
import {AutoDialer} from "types/auto-dialer"
import {Settings} from "types/settings"
import {Zoom} from "types/zoom"
import {useMutation} from "@tanstack/react-query"
import {StudentCallLog} from "types/student-call-log"
import {DialerStudent} from "types/auto-dialer"

type ActiveCall = TwilioCall | TelnyxCall

type StartCallOptions = {
    departmentId?: number
    retryCount?: number
    fromPhone?: string
}

type ContextProps = {
    calling: boolean
    voiceProvider: Settings.DialerProvider
    startCall: (student: DialerStudent, options?: StartCallOptions) => void
    hangup: () => void
    activeStudent: DialerStudent | null
    activeDepartmentId: number | null
    queuedStudents: DialerStudent[]
    setQueuedStudents: (students: DialerStudent[]) => void
    callStatus: string
    isConnected: boolean
    isAuto: boolean
    setIsAuto: (isAuto: boolean) => void
    acceptCall: () => void
    rejectCall: () => void
    isIncomingCall: boolean
    isInTransitionCall: boolean
    transitionCountdown: number
    connectedTime: Date | null
    forceCloseCall: () => void
    onEndedCallback: (callback: () => void) => void
    toggleMute: () => void
    isMuted: boolean
    activeCall: ActiveCall | null
}

export const VoiceCallContext = createContext<ContextProps>({} as ContextProps)

type VoiceCallProviderProps = {
    children: React.ReactNode
}

let device: Device | null = null
let telnyxClient: TelnyxRTC | null = null

// Helper function to parse URL-encoded query string
const parseQueryString = (queryString: string): Record<string, string> => {
    const params = new URLSearchParams(queryString)
    const result: Record<string, string> = {}

    for (const [key, value] of params.entries()) {
        result[key] = value
    }

    return result
}

const normalizeHeaderName = (name: string): string => name.replace(/^x-/i, "").toLowerCase()

const headerEntries = (headers: any): Array<[string, string]> => {
    if (!headers) return []
    if (typeof headers.entries === "function") {
        return Array.from(headers.entries() as Iterable<[unknown, unknown]>).map(([key, value]) => [
            String(key),
            value == null ? "" : String(value)
        ])
    }
    if (typeof headers.get === "function" && typeof headers.keys === "function") {
        const entries: Array<[string, string]> = []
        for (const key of headers.keys()) {
            const value = headers.get(key)
            if (value != null && value !== "") {
                entries.push([String(key), String(value)])
            }
        }
        return entries
    }
    if (Array.isArray(headers)) {
        return headers
            .map(
                (item) => [String(item?.name || ""), item?.value == null ? "" : String(item.value)] as [string, string]
            )
            .filter(([, value]) => value !== "")
    }
    return Object.entries(headers)
        .map(([key, value]) => [key, value == null ? "" : String(value)] as [string, string])
        .filter(([, value]) => value !== "")
}

const toE164Phone = (phone?: string): string => {
    if (!phone) return ""
    const digits = String(phone).replace(/[^\d]/g, "")
    if (!digits) return String(phone)
    return `+${digits}`
}

const getHeaderValue = (headers: any, name: string): string | undefined => {
    if (!headers) return undefined
    const wanted = normalizeHeaderName(name)
    for (const [key, value] of headerEntries(headers)) {
        if (value !== "" && normalizeHeaderName(key) === wanted) {
            return value
        }
    }
    return undefined
}

const TELNYX_SESSION_STORAGE_KEY = "edular-telnyx-softphone-session"
const TELNYX_SESSION_CHANNEL = "edular-telnyx-softphone-session"
/** Telnyx WebRTC only plays remote audio when an HTMLMediaElement is attached. */
const TELNYX_REMOTE_AUDIO_ELEMENT_ID = "edular-telnyx-remote-audio"

const ensureTelnyxRemoteAudioElement = (): HTMLAudioElement => {
    const existing = document.getElementById(TELNYX_REMOTE_AUDIO_ELEMENT_ID)
    if (existing instanceof HTMLAudioElement) {
        existing.autoplay = true
        existing.setAttribute("playsinline", "true")
        return existing
    }
    const audio = document.createElement("audio")
    audio.id = TELNYX_REMOTE_AUDIO_ELEMENT_ID
    audio.autoplay = true
    audio.setAttribute("playsinline", "true")
    // Keep in DOM for sinkId / autoplay; hide from layout.
    audio.style.display = "none"
    document.body.appendChild(audio)
    return audio
}

const bindTelnyxRemoteAudio = (client: TelnyxRTC) => {
    const remoteAudio = ensureTelnyxRemoteAudioElement()
    client.remoteElement = remoteAudio
    return remoteAudio
}

const playTelnyxRemoteAudio = () => {
    const audio = document.getElementById(TELNYX_REMOTE_AUDIO_ELEMENT_ID)
    if (!(audio instanceof HTMLAudioElement)) {
        return
    }
    void audio.play().catch((error) => {
        console.log("[Telnyx] remote audio play blocked", error)
    })
}

const createTelnyxTabSessionId = (): string => {
    const created = (crypto.randomUUID?.() || `${Date.now()}${Math.random().toString(36).slice(2)}`).replace(
        /[^A-Za-z0-9_-]/g,
        ""
    )
    return created.slice(0, 64) || `tab${Date.now()}`
}

const readStoredTelnyxTabSessionId = (): string => {
    try {
        const existing = sessionStorage.getItem(TELNYX_SESSION_STORAGE_KEY)
        if (existing && /^[A-Za-z0-9_-]{1,64}$/.test(existing)) {
            return existing
        }
    } catch {
        // ignore
    }
    return ""
}

const persistTelnyxTabSessionId = (sessionId: string) => {
    try {
        sessionStorage.setItem(TELNYX_SESSION_STORAGE_KEY, sessionId)
    } catch {
        // ignore
    }
}

/**
 * Duplicate-tab copies sessionStorage, so two tabs would share one SIP registration.
 * Claim the session id over BroadcastChannel; if another tab already owns it, mint a new id.
 * Refresh keeps the stored id because no other tab answers the claim.
 */
const useTelnyxTabSessionId = (enabled: boolean): string => {
    const [sessionId, setSessionId] = useState("")
    const sessionIdRef = useRef("")
    const instanceIdRef = useRef(createTelnyxTabSessionId())

    useEffect(() => {
        if (!enabled) {
            return
        }
        const instanceId = instanceIdRef.current
        const candidate = readStoredTelnyxTabSessionId() || createTelnyxTabSessionId()
        let contested = false

        if (typeof BroadcastChannel === "undefined") {
            const fallback = createTelnyxTabSessionId()
            persistTelnyxTabSessionId(fallback)
            sessionIdRef.current = fallback
            setSessionId(fallback)
            return
        }

        const channel = new BroadcastChannel(TELNYX_SESSION_CHANNEL)
        const onMessage = (event: MessageEvent) => {
            const data = event.data
            if (!data || data.instanceId === instanceId) {
                return
            }
            if (data.type === "claim" && data.sessionId === sessionIdRef.current) {
                channel.postMessage({type: "taken", sessionId: sessionIdRef.current, instanceId})
            }
            if (data.type === "claim" && data.sessionId === candidate && !sessionIdRef.current) {
                if (String(data.instanceId) < String(instanceId)) {
                    contested = true
                }
            }
            if (data.type === "taken" && data.sessionId === candidate && !sessionIdRef.current) {
                contested = true
            }
        }
        channel.addEventListener("message", onMessage)
        channel.postMessage({type: "claim", sessionId: candidate, instanceId})

        const timer = window.setTimeout(() => {
            const next = contested ? createTelnyxTabSessionId() : candidate
            persistTelnyxTabSessionId(next)
            sessionIdRef.current = next
            setSessionId(next)
        }, 80)

        return () => {
            window.clearTimeout(timer)
            channel.removeEventListener("message", onMessage)
            channel.close()
        }
    }, [enabled])

    return sessionId
}

export const VoiceCallProvider: React.FC<VoiceCallProviderProps> = ({children}) => {
    // information
    const [activeStudent, setActiveStudent] = useState<DialerStudent | undefined>(undefined)
    const [queuedStudents, setQueuedStudents] = useState<DialerStudent[]>([])
    const [activeDepartmentId, setActiveDepartmentId] = useState<number | undefined>()
    const [isAuto, setIsAuto] = useState(false)

    // status
    const [calling, setCalling] = useState(false)
    const [deviceStatus, setDeviceStatus] = useState<string>()
    const [callStatus, setCallStatus] = useState<string>()
    const [activeCall, setActiveCall] = useState<ActiveCall | null>(null)
    const [isIncomingCall, setIsIncomingCall] = useState(false)
    const onEndedCallRef = useRef<(() => void) | null>(null)
    const [isMuted, setIsMuted] = useState(false)

    // provider
    const [zoomAccount, setZoomAccount] = useState<Zoom.ZoomAccount | undefined>()

    const model = useModel()
    const voiceProvider = model.clientSetting.dialerProvider
    const telnyxTabSessionId = useTelnyxTabSessionId(voiceProvider === Settings.DialerProvider.Telnyx)
    const slug = model.slug
    const staffProfileId = model.profileId
    const isConnected = [AutoDialer.DialerStatus.Connected, AutoDialer.DialerStatus.InProgress].includes(
        callStatus as AutoDialer.DialerStatus
    )
    const [isInTransitionCall, setIsInTransitionCall] = useState(false)
    const [transitionCountdown, setTransitionCountdown] = useState(10)
    const [connectedTime, setConnectedTime] = useState<Date | null>(null)
    const [isEnded, setIsEnded] = useState(false)
    const activeStudentRef = useRef<DialerStudent | null | undefined>(undefined)
    const callingRef = useRef(false)
    const activeCallRef = useRef<ActiveCall | null>(null)
    const telnyxRefreshInFlightRef = useRef(false)
    const pendingTelnyxRefreshRef = useRef(false)
    const inboundRingIdRef = useRef<string | null>(null)
    const isTwilioEndHandledRef = useRef<boolean>(false)
    /** Set when Twilio `accept` fired — bridged session (outbound uses answerOnBridge TwiML). */
    const twilioHadMediaConnectedRef = useRef(false)
    const disabledPreventLeave = model.slug === "nau" && voiceProvider === Settings.DialerProvider.Zoom

    useEffect(() => {
        callingRef.current = calling
    }, [calling])

    useEffect(() => {
        activeCallRef.current = activeCall
    }, [activeCall])

    useEffect(() => {
        return () => {
            if (!telnyxClient) {
                return
            }
            try {
                ;(activeCallRef.current as TelnyxCall | null)?.hangup?.()
            } catch {
                // ignore
            }
            try {
                telnyxClient.disconnect()
            } catch {
                // ignore
            }
            telnyxClient = null
        }
    }, [])

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (calling && !disabledPreventLeave) {
                e.preventDefault()
                return "You have an active call. Are you sure you want to leave?"
            }
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload)
        }
    }, [calling, disabledPreventLeave])

    useEffect(() => {
        const shouldFlash = isIncomingCall && !isConnected
        if (!shouldFlash) {
            return
        }

        const incomingLabel = "Incoming call"
        const baseTitle = document.title.startsWith("Incoming call")
            ? document.title.replace(/^Incoming call(?:: [^—]*)?(?: — )?/, "") || "KlassApp"
            : document.title

        let showIncoming = true
        document.title = incomingLabel
        const interval = window.setInterval(() => {
            showIncoming = !showIncoming
            document.title = showIncoming ? incomingLabel : baseTitle
        }, 1000)

        return () => {
            window.clearInterval(interval)
            document.title = baseTitle
        }
    }, [isIncomingCall, isConnected])

    useQuery({
        queryKey: ["zoom-account"],
        queryFn: () => zoomService.getMyZoomAccount(),
        enabled: voiceProvider === Settings.DialerProvider.Zoom,
        refetchOnMount: true,
        onSuccess: (data) => {
            setZoomAccount(data)
        }
    })

    const resetCall = useCallback(() => {
        isTwilioEndHandledRef.current = false
        twilioHadMediaConnectedRef.current = false
        inboundRingIdRef.current = null
        setCalling(false)
        setCallStatus(AutoDialer.DialerStatus.Ended)
        setIsIncomingCall(false)
        activeStudentRef.current = null
        setActiveStudent(null)
        setActiveDepartmentId(null)
        setActiveCall(null)
        setIsMuted(false)
        setIsAuto(false)
    }, [])

    const handleDialerStatus = useCallback(
        (payload: AutoDialer.DialerSocketPayload) => {
            const currentStudent = activeStudentRef.current
            if (
                staffProfileId === payload.profileId &&
                (currentStudent?.profileId === payload.studentProfileId || currentStudent?.phone === payload.phone)
            ) {
                const {status: dialerStatus, isEnded, errorMessage} = payload
                setCallStatus(dialerStatus)
                setIsEnded(!!isEnded)
                if (!!errorMessage && isEnded) {
                    toastInfo(errorMessage)
                }
                if (
                    [AutoDialer.DialerStatus.NoAnswer, AutoDialer.DialerStatus.Busy].includes(
                        dialerStatus as AutoDialer.DialerStatus
                    )
                ) {
                    toastInfo("The student is not available. Please try again later.")
                }
                if (
                    [AutoDialer.DialerStatus.Connected, AutoDialer.DialerStatus.InProgress].includes(
                        dialerStatus as AutoDialer.DialerStatus
                    ) &&
                    payload.connectedAt
                ) {
                    setConnectedTime(new Date(payload.connectedAt))
                }
            }
        },
        [staffProfileId]
    )

    const buildTwilioSyntheticPayload = useCallback(
        (
            partial: Partial<AutoDialer.DialerSocketPayload> & {
                status: string
            }
        ): AutoDialer.DialerSocketPayload | null => {
            const student = activeStudentRef.current
            if (!student?.profileId && !student?.phone) return null
            return {
                profileId: staffProfileId,
                studentProfileId: student.profileId,
                phone: student.phone,
                ringingAt: partial.ringingAt,
                connectedAt: partial.connectedAt,
                endedAt: partial.endedAt,
                status: partial.status,
                isEnded: partial.isEnded ?? false,
                errorMessage: partial.errorMessage
            }
        },
        [staffProfileId]
    )

    const applyDialerSocketPayload = useCallback(
        (payload: AutoDialer.DialerSocketPayload | null) => {
            if (payload) handleDialerStatus(payload)
        },
        [handleDialerStatus]
    )

    const finalizeTwilioCallEnd = useCallback(
        (opts?: {forceStatus?: string; errorMessage?: string}) => {
            if (isTwilioEndHandledRef.current) return
            isTwilioEndHandledRef.current = true
            const now = new Date().toISOString()
            let status = opts?.forceStatus
            if (!status) {
                if (opts?.errorMessage) {
                    status = "failed"
                } else {
                    status = twilioHadMediaConnectedRef.current ? "completed" : "canceled"
                }
            }
            applyDialerSocketPayload(
                buildTwilioSyntheticPayload({
                    status,
                    endedAt: now,
                    isEnded: true,
                    errorMessage: opts?.errorMessage
                })
            )
        },
        [applyDialerSocketPayload, buildTwilioSyntheticPayload]
    )

    const subscribeTwilioCallDialerUi = useCallback(
        (
            call: TwilioCall,
            ctx: {
                retryCount: number
                student: DialerStudent
                departmentId?: number
                onRetryConnectionError?: () => void
            }
        ) => {
            const {retryCount, onRetryConnectionError} = ctx
            call.on("ringing", () => {
                applyDialerSocketPayload(
                    buildTwilioSyntheticPayload({
                        status: "ringing",
                        ringingAt: new Date().toISOString(),
                        isEnded: false
                    })
                )
            })

            call.on("accept", () => {
                twilioHadMediaConnectedRef.current = true
                applyDialerSocketPayload(
                    buildTwilioSyntheticPayload({
                        status: "in-progress",
                        connectedAt: new Date().toISOString(),
                        isEnded: false
                    })
                )
            })

            call.on("reconnecting", () => {
                applyDialerSocketPayload(
                    buildTwilioSyntheticPayload({
                        status: "reconnecting",
                        isEnded: false
                    })
                )
            })

            call.on("reconnected", () => {
                applyDialerSocketPayload(
                    buildTwilioSyntheticPayload({
                        status: twilioHadMediaConnectedRef.current ? "in-progress" : "ringing",
                        isEnded: false
                    })
                )
            })

            const endOutboundCall = () => {
                finalizeTwilioCallEnd()
            }

            call.on("disconnect", endOutboundCall)
            call.on("cancel", endOutboundCall)

            call.on("error", (error: {code?: number; message?: string}) => {
                console.log("[Twilio] Call Error", error)
                if (error.code === 31005 && retryCount < 1 && onRetryConnectionError) {
                    setActiveCall(null)
                    toastInfo("Connection error. Retrying call...")
                    setTimeout(() => {
                        onRetryConnectionError()
                    }, 500)
                    return
                }
                if (error.code === 31401) {
                    toastError(
                        "Please allow microphone access and then try again.",
                        "You need to grant microphone permission in your browser for this website in order to make calls.",
                        {
                            duration: 5
                        }
                    )
                    resetCall()
                    return
                }
                finalizeTwilioCallEnd({errorMessage: error.message})
            })

            call.on("mute", (muted) => setIsMuted(muted))
        },
        [applyDialerSocketPayload, buildTwilioSyntheticPayload, finalizeTwilioCallEnd, resetCall]
    )

    useQuery({
        queryKey: ["twilio-token"],
        queryFn: () => autoDialerService.getTwilioToken(),
        enabled: voiceProvider === Settings.DialerProvider.Twilio,
        refetchOnMount: true,
        onSuccess: (data) => {
            device = new Device(data.token)

            device.on("error", (error) => {
                // Unhandled error
                console.log("[Twilio] Error", error)
            })
            device.on("registered", () => setDeviceStatus("connected"))
            device.on("tokenWillExpire", async () => {
                console.log("[Twilio] Token will expire soon, refreshing...")
                const {token} = await autoDialerService.getTwilioToken()
                device.updateToken(token)
                console.log("[Twilio] Token updated")
            })
            device.on("destroyed", () => {
                setDeviceStatus("disconnected")
                setCalling(false)
            })
            device.on("incoming", (call) => {
                const student = call.parameters.Params
                const studentParams = parseQueryString(student)
                const mappedStudent: DialerStudent = {
                    userId: parseInt(studentParams.userId),
                    profileId: parseInt(studentParams.profileId),
                    fullName: studentParams.fullName,
                    email: studentParams.email || "", // Default empty string if email not provided
                    phone: studentParams.phone,
                    campusName: studentParams.campusName
                }
                setActiveDepartmentId(parseInt(studentParams.departmentId))
                activeStudentRef.current = mappedStudent
                setActiveStudent(mappedStudent)
                setCallStatus(AutoDialer.DialerStatus.Initiated)
                setCalling(true)
                setIsIncomingCall(true)

                isTwilioEndHandledRef.current = false
                twilioHadMediaConnectedRef.current = false

                call.on("ringing", () => {
                    applyDialerSocketPayload(
                        buildTwilioSyntheticPayload({
                            status: "ringing",
                            ringingAt: new Date().toISOString(),
                            isEnded: false
                        })
                    )
                })
                call.on("accept", () => {
                    twilioHadMediaConnectedRef.current = true
                    applyDialerSocketPayload(
                        buildTwilioSyntheticPayload({
                            status: "in-progress",
                            connectedAt: new Date().toISOString(),
                            isEnded: false
                        })
                    )
                })
                call.on("disconnect", () => finalizeTwilioCallEnd())
                call.on("cancel", resetCall)
                call.on("mute", (muted) => setIsMuted(muted))
                setActiveCall(call)
            })

            device.register()
        }
    })

    const createAndBindTelnyxClient = useCallback(
        (token: string) => {
            if (telnyxClient) {
                try {
                    telnyxClient.disconnect()
                } catch {
                    // ignore
                }
            }
            telnyxClient = new TelnyxRTC({login_token: token})
            // Without remoteElement, WebRTC receives the student stream but never plays it
            // (student still hears staff; Telnyx recording still has both sides).
            bindTelnyxRemoteAudio(telnyxClient)

            telnyxClient.on("telnyx.error", (error) => {
                console.log("[Telnyx] Error", error)
            })
            telnyxClient.on("telnyx.ready", () => setDeviceStatus("connected"))
            telnyxClient.on("telnyx.socket.close", () => {
                setDeviceStatus("disconnected")
            })

            const refreshToken = async (opts?: {force?: boolean}) => {
                if (telnyxRefreshInFlightRef.current) {
                    return
                }
                if (!opts?.force && callingRef.current) {
                    pendingTelnyxRefreshRef.current = true
                    console.log("[Telnyx] Token expiring during active call; refresh deferred")
                    return
                }
                telnyxRefreshInFlightRef.current = true
                pendingTelnyxRefreshRef.current = false
                try {
                    console.log("[Telnyx] Token expiring, refreshing...")
                    const refreshed = await autoDialerService.getTelnyxToken(telnyxTabSessionId)
                    createAndBindTelnyxClient(refreshed.token)
                    console.log("[Telnyx] Token updated")
                } catch (error) {
                    console.log("[Telnyx] Token refresh failed", error)
                } finally {
                    telnyxRefreshInFlightRef.current = false
                }
            }
            telnyxClient.on("telnyx.token.willExpire" as any, refreshToken)
            ;(telnyxClient as any).on?.("tokenWillExpire", refreshToken)

            telnyxClient.on("telnyx.notification", (notification: any) => {
                const call = notification?.call
                if (!call) return

                if (notification.type === "callUpdate") {
                    const state = call.state
                    if (state === "ringing" && call.direction === "inbound") {
                        const headers = call.options?.customHeaders || call.customHeaders || {}
                        const fallbackPhone = toE164Phone(
                            getHeaderValue(headers, "phone") ||
                                call.options?.remoteCallerNumber ||
                                call.remoteCallerNumber ||
                                ""
                        )
                        const mappedStudent: DialerStudent = {
                            userId: parseInt(getHeaderValue(headers, "userId") || "0"),
                            profileId: parseInt(getHeaderValue(headers, "profileId") || "0"),
                            fullName: getHeaderValue(headers, "fullName") || "Unknown caller",
                            email: "",
                            phone: fallbackPhone,
                            campusName: getHeaderValue(headers, "campusName")
                        }
                        const departmentId = parseInt(getHeaderValue(headers, "departmentId") || "0")
                        const ringId = String(call.id || fallbackPhone || "")
                        inboundRingIdRef.current = ringId
                        if (departmentId) setActiveDepartmentId(departmentId)
                        activeStudentRef.current = mappedStudent
                        setActiveStudent(mappedStudent)
                        setCallStatus(AutoDialer.DialerStatus.Initiated)
                        setCalling(true)
                        setIsIncomingCall(true)
                        isTwilioEndHandledRef.current = false
                        twilioHadMediaConnectedRef.current = false
                        setActiveCall(call)
                        void autoDialerService
                            .getTelnyxIncomingCaller(fallbackPhone)
                            .then((pending) => {
                                if (!pending) return
                                if (inboundRingIdRef.current !== ringId) return
                                if (["hangup", "destroy", "purge"].includes(call.state)) return
                                const fromApi: DialerStudent = {
                                    userId: pending.userId ? Number(pending.userId) : mappedStudent.userId,
                                    profileId: pending.profileId ? Number(pending.profileId) : mappedStudent.profileId,
                                    fullName: pending.fullName || mappedStudent.fullName,
                                    email: "",
                                    phone: toE164Phone(pending.phone || mappedStudent.phone),
                                    campusName: pending.campusName || mappedStudent.campusName
                                }
                                const headerPhone = toE164Phone(mappedStudent.phone)
                                const apiPhone = toE164Phone(fromApi.phone)
                                if (headerPhone && apiPhone && headerPhone !== apiPhone) return
                                if (
                                    mappedStudent.profileId &&
                                    fromApi.profileId &&
                                    mappedStudent.profileId !== fromApi.profileId
                                ) {
                                    return
                                }
                                activeStudentRef.current = fromApi
                                setActiveStudent(fromApi)
                                if (pending.departmentId) {
                                    setActiveDepartmentId(Number(pending.departmentId))
                                }
                            })
                            .catch((error) => {
                                console.log("[Telnyx] incoming caller lookup failed", error)
                            })
                    }

                    if (state === "active" || state === "answered") {
                        twilioHadMediaConnectedRef.current = true
                        playTelnyxRemoteAudio()
                        applyDialerSocketPayload(
                            buildTwilioSyntheticPayload({
                                status: "in-progress",
                                connectedAt: new Date().toISOString(),
                                isEnded: false
                            })
                        )
                    }
                    if (state === "ringing" && call.direction === "outbound") {
                        applyDialerSocketPayload(
                            buildTwilioSyntheticPayload({
                                status: "ringing",
                                ringingAt: new Date().toISOString(),
                                isEnded: false
                            })
                        )
                    }
                    if (["hangup", "destroy", "purge"].includes(state)) {
                        inboundRingIdRef.current = null
                        finalizeTwilioCallEnd()
                        setActiveCall(null)
                        if (pendingTelnyxRefreshRef.current) {
                            void refreshToken({force: true})
                        }
                    }
                }
            })

            telnyxClient.connect()
        },
        [applyDialerSocketPayload, buildTwilioSyntheticPayload, finalizeTwilioCallEnd, telnyxTabSessionId]
    )

    useQuery({
        queryKey: ["telnyx-token", telnyxTabSessionId],
        queryFn: () => autoDialerService.getTelnyxToken(telnyxTabSessionId),
        enabled: voiceProvider === Settings.DialerProvider.Telnyx && Boolean(telnyxTabSessionId),
        refetchOnMount: true,
        staleTime: 0,
        onSuccess: (data) => {
            createAndBindTelnyxClient(data.token)
        }
    })

    const resolveCallerIdPhone = useCallback(
        (student: DialerStudent, departmentId?: number) => {
            const assignedPhoneNumber = model.smsPhoneNumbers.find((phone) => phone.userId === model.user.id)
            if (!assignedPhoneNumber) {
                const departmentAllPhoneNumbers = model.smsAllPhoneNumbers.filter(
                    (phone) => phone.departmentIdForDialer === departmentId
                )
                if (departmentAllPhoneNumbers.length <= 0) {
                    return null
                }
                const campusPhoneNumbers = student.campusId
                    ? departmentAllPhoneNumbers.filter((phone) =>
                          phone.campuses.some((campus) => campus.id === student.campusId)
                      )
                    : []
                return campusPhoneNumbers.length > 0 ? campusPhoneNumbers[0] : departmentAllPhoneNumbers[0]
            }
            return assignedPhoneNumber
        },
        [model.smsAllPhoneNumbers, model.smsPhoneNumbers, model.user.id]
    )

    const startCall = useCallback(
        (student: DialerStudent, options: StartCallOptions = {}) => {
            const {departmentId, retryCount = 0, fromPhone} = options
            setIsInTransitionCall(false)
            setTransitionCountdown(0)
            setConnectedTime(null)
            isTwilioEndHandledRef.current = false
            twilioHadMediaConnectedRef.current = false
            setIsEnded(false) // clear previous call's ended state so effect doesn't call resetCall()
            activeStudentRef.current = student
            setActiveStudent(student)
            setActiveDepartmentId(departmentId)

            if (voiceProvider === Settings.DialerProvider.Twilio) {
                const fromPhoneNumber = fromPhone ? {phone: fromPhone} : resolveCallerIdPhone(student, departmentId)
                if (!fromPhoneNumber?.phone) {
                    toastInfo("You haven't set up a phone number for this department")
                    resetCall()
                    return
                }

                let params: any = {
                    from: fromPhoneNumber.phone,
                    to: student.phone,
                    staffProfileId: staffProfileId.toString(),
                    slug
                }
                if (student.profileId) {
                    params.studentProfileId = student.profileId.toString()
                }
                if (departmentId) {
                    params.departmentId = departmentId.toString()
                }
                if (student.dialerId) {
                    params.dialerId = student.dialerId.toString()
                }
                device.connect({params}).then((call) => {
                    subscribeTwilioCallDialerUi(call, {
                        retryCount,
                        student,
                        departmentId,
                        onRetryConnectionError: () =>
                            startCall(student, {departmentId, retryCount: retryCount + 1, fromPhone})
                    })
                    setActiveCall(call)
                })
                setCalling(true)
                setCallStatus(AutoDialer.DialerStatus.Queued)
            }
            if (voiceProvider === Settings.DialerProvider.Telnyx) {
                const fromPhoneNumber = fromPhone ? {phone: fromPhone} : resolveCallerIdPhone(student, departmentId)
                if (!fromPhoneNumber?.phone) {
                    toastInfo("You haven't set up a phone number for this department")
                    resetCall()
                    return
                }
                if (!telnyxClient) {
                    toastError("Telnyx softphone is not ready yet. Please wait a moment and try again.")
                    resetCall()
                    return
                }

                const clientStatePayload: Record<string, string> = {
                    from: fromPhoneNumber.phone,
                    to: student.phone,
                    staffProfileId: staffProfileId.toString(),
                    slug
                }
                if (student.profileId) {
                    clientStatePayload.studentProfileId = student.profileId.toString()
                }
                if (departmentId) {
                    clientStatePayload.departmentId = departmentId.toString()
                }
                if (student.dialerId) {
                    clientStatePayload.dialerId = student.dialerId.toString()
                }

                const encodedClientState = btoa(JSON.stringify(clientStatePayload))
                try {
                    const remoteAudio = bindTelnyxRemoteAudio(telnyxClient)
                    const call = telnyxClient.newCall({
                        callerNumber: fromPhoneNumber.phone,
                        destinationNumber: student.phone,
                        audio: true,
                        remoteElement: remoteAudio,
                        clientState: encodedClientState,
                        customHeaders: [
                            {
                                name: "X-Edular-Client-State",
                                value: encodedClientState
                            }
                        ]
                    } as any)
                    setActiveCall(call)
                    setCalling(true)
                    setCallStatus(AutoDialer.DialerStatus.Queued)
                    applyDialerSocketPayload(
                        buildTwilioSyntheticPayload({
                            status: "ringing",
                            ringingAt: new Date().toISOString(),
                            isEnded: false
                        })
                    )
                } catch (error: any) {
                    console.log("[Telnyx] Call Error", error)
                    if (retryCount < 1) {
                        toastInfo("Connection error. Retrying call...")
                        setTimeout(() => startCall(student, {departmentId, retryCount: retryCount + 1, fromPhone}), 500)
                        return
                    }
                    finalizeTwilioCallEnd({errorMessage: error?.message})
                }
            }
            if (voiceProvider === Settings.DialerProvider.Zoom) {
                if (!zoomAccount) {
                    toastInfo(
                        "You have not connected your Zoom account yet",
                        "Now we will redirect you to connect your account. After that, you can start the call."
                    )
                    setTimeout(() => {
                        grantZoomAccountMutation.mutate()
                    }, 1000)
                    return
                }
                logZoomCallMutation.mutate({
                    profileId: student.profileId,
                    phone: student.phone,
                    departmentId,
                    provider: Settings.DialerProvider.Zoom,
                    dialerId: student.dialerId
                } as any)
            }
        },
        [
            voiceProvider,
            zoomAccount,
            resolveCallerIdPhone,
            staffProfileId,
            slug,
            resetCall,
            subscribeTwilioCallDialerUi,
            applyDialerSocketPayload,
            buildTwilioSyntheticPayload,
            finalizeTwilioCallEnd
        ]
    )

    const nextCall = useCallback(() => {
        const [nextStudent, ...rest] = queuedStudents
        setQueuedStudents(rest)
        if (nextStudent) {
            startCall(nextStudent, {departmentId: activeDepartmentId})
        } else {
            setIsAuto(false)
            resetCall()
        }
    }, [queuedStudents, activeDepartmentId, startCall])

    const onEndedCall = useCallback(() => {
        if (isAuto && queuedStudents.length > 0) {
            setIsInTransitionCall(true)
            setTimeout(nextCall, 10000)
        } else {
            resetCall()
        }
        setIsEnded(false)
        onEndedCallRef.current?.()
    }, [isAuto, queuedStudents.length, nextCall, resetCall, onEndedCallRef])

    useEffect(() => {
        if (isInTransitionCall) {
            setTransitionCountdown(10)
            const interval = setInterval(() => {
                setTransitionCountdown((prev) => {
                    if (prev > 1) return prev - 1
                    clearInterval(interval)
                    return 1
                })
            }, 1000)
            return () => clearInterval(interval)
        }
    }, [isInTransitionCall])

    useEffect(() => {
        if (isEnded) {
            onEndedCall()
        }
    }, [isEnded, onEndedCall])

    useEffect(() => {
        if (voiceProvider !== Settings.DialerProvider.Zoom) return
        coreSocket.on(SocketEvent.DialerUpdated, handleDialerStatus)
        return () => {
            coreSocket.off(SocketEvent.DialerUpdated, handleDialerStatus)
        }
    }, [voiceProvider, handleDialerStatus])

    const hangup = useCallback(() => {
        if (voiceProvider === Settings.DialerProvider.Telnyx) {
            try {
                ;(activeCall as TelnyxCall | null)?.hangup?.()
            } catch {
                // ignore
            }
        } else {
            device?.disconnectAll()
        }
        setCallStatus(AutoDialer.DialerStatus.Ended)
        setActiveCall(null)
        if (isIncomingCall) resetCall()
    }, [voiceProvider, activeCall, isIncomingCall, resetCall])

    const acceptCall = useCallback(() => {
        if (voiceProvider === Settings.DialerProvider.Telnyx) {
            const remoteAudio = telnyxClient ? bindTelnyxRemoteAudio(telnyxClient) : ensureTelnyxRemoteAudioElement()
            ;(activeCall as TelnyxCall | null)?.answer?.({remoteElement: remoteAudio} as any)
            playTelnyxRemoteAudio()
            return
        }
        ;(activeCall as TwilioCall | null)?.accept()
    }, [activeCall, voiceProvider])

    const rejectCall = useCallback(() => {
        if (voiceProvider === Settings.DialerProvider.Telnyx) {
            ;(activeCall as TelnyxCall | null)?.hangup?.()
        } else {
            ;(activeCall as TwilioCall | null)?.reject()
        }
        resetCall()
    }, [activeCall, voiceProvider, resetCall])

    const grantZoomAccountMutation = useMutation({
        mutationFn: () =>
            zoomService.grantAccount({
                backUrl: window.location.href
            }),
        onSuccess: (authorizedData) => {
            if (authorizedData && authorizedData.redirect === true) {
                window.location.href = authorizedData.redirectUri
            }
        },
        onError: (error) => {
            handleError(error)
        }
    })

    const logZoomCallMutation = useMutation({
        mutationFn: (params) => studentCallLogService.add(params),
        onSuccess: (data: StudentCallLog) => {
            setCallStatus(AutoDialer.DialerStatus.Initiated)
            window.open(`zoomphonecall://${data.calleePhoneNumber}`, "_blank", "noopener,noreferrer")
            setCalling(true)
        },
        onError: (error) => {
            handleError(error)
        }
    })

    const forceCloseCall = useCallback(() => {
        if (voiceProvider === Settings.DialerProvider.Telnyx) {
            try {
                ;(activeCall as TelnyxCall | null)?.hangup?.()
            } catch {
                // ignore
            }
        } else {
            device?.disconnectAll()
        }
        resetCall()
    }, [voiceProvider, activeCall, resetCall])

    const onEndedCallback = (f: () => void) => {
        onEndedCallRef.current = f
    }

    const toggleMute = useCallback(() => {
        if (!isBrowserSoftphone(voiceProvider) || !activeCall) return
        if (voiceProvider === Settings.DialerProvider.Telnyx) {
            const telnyxCall = activeCall as TelnyxCall
            if (isMuted) {
                telnyxCall.unmuteAudio?.()
            } else {
                telnyxCall.muteAudio?.()
            }
            setIsMuted(!isMuted)
            return
        }
        ;(activeCall as TwilioCall).mute(!isMuted)
    }, [isMuted, activeCall, voiceProvider])

    return (
        <VoiceCallContext.Provider
            value={{
                calling,
                voiceProvider,
                startCall,
                hangup,
                activeStudent,
                activeDepartmentId,
                queuedStudents,
                setQueuedStudents,
                callStatus,
                isConnected,
                isAuto,
                setIsAuto,
                acceptCall,
                rejectCall,
                isIncomingCall,
                isInTransitionCall,
                transitionCountdown,
                connectedTime,
                forceCloseCall,
                onEndedCallback,
                toggleMute,
                isMuted,
                activeCall
            }}>
            {children}
        </VoiceCallContext.Provider>
    )
}
